import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import type { RootState } from 'common/store/store';

const lockTimeoutDefault = 5 * 60 * 1000; // 5 minutes for idle timeout
const tabAwayTimeoutDefault = 5 * 60 * 1000; // 5 minutes for tab away / app reopen

export interface LockState {
  isLocked: boolean;
  lockEnabled: boolean;
  webAuthSetUp: boolean;
  webAuthCredentialId: string | null;
  lastActivity: number;
  lockTimeout: number;
  tabAwayTimeout: number;
  showSetupPrompt: boolean;
  // One-shot: Welcome sets it on the cold-start handoff so NativeLockCover
  // prompts for Face ID as soon as it is up, instead of waiting for a tap.
  // Not persisted (REHYDRATE below never copies it), so it cannot replay.
  autoUnlockRequested: boolean;
}

const initialState: LockState = {
  isLocked: false,
  lockEnabled: true, // Default ON for all users
  webAuthSetUp: false,
  webAuthCredentialId: null,
  lastActivity: Date.now(),
  lockTimeout: lockTimeoutDefault,
  tabAwayTimeout: tabAwayTimeoutDefault,
  showSetupPrompt: false,
  autoUnlockRequested: false,
};

const lockSlice = createSlice({
  name: 'lock',
  initialState,
  reducers: {
    lockSession: (state) => {
      state.isLocked = true;
    },
    unlockSession: (state) => {
      state.isLocked = false;
      state.lastActivity = Date.now();
      state.autoUnlockRequested = false;
    },
    requestAutoUnlock: (state) => {
      state.autoUnlockRequested = true;
    },
    clearAutoUnlock: (state) => {
      state.autoUnlockRequested = false;
    },
    setLockEnabled: (state, { payload }: PayloadAction<boolean>) => {
      state.lockEnabled = payload;
      // If disabling, also unlock
      if (!payload) {
        state.isLocked = false;
      }
    },
    setWebAuthSetUp: (state, { payload }: PayloadAction<{ isSetUp: boolean; credentialId?: string }>) => {
      state.webAuthSetUp = payload.isSetUp;
      state.webAuthCredentialId = payload.credentialId || null;
    },
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },
    setLockTimeout: (state, { payload }: PayloadAction<number>) => {
      state.lockTimeout = payload;
    },
    setShowSetupPrompt: (state, { payload }: PayloadAction<boolean>) => {
      state.showSetupPrompt = payload;
    },
    // Removed dead `resetLockState` action (never dispatched anywhere).
    // Considered wiring it into logout, but both fields it reset are already
    // guaranteed correct by the time a logout can happen: `isLocked` and
    // `showSetupPrompt` both gate `shouldBlur` (app.tsx / LockScreen), so the
    // user can't reach the menu's Logout action while either is true — every
    // logout path already exits with both false. `lastActivity` doesn't need
    // resetting either: useActivityTracker's effect depends on `hasToken` and
    // re-stamps `lastActivity = now` on its own the moment a fresh login
    // flips that boolean, unlocked-boot case included.
  },
  extraReducers: (builder) => {
    // On rehydrate, decide whether the session boots locked.
    //
    // Chosen lock semantics (web):
    // 1. Guests (no auth token) NEVER lock — they have no credential to unlock
    //    with; LockScreen and useActivityTracker both no-op for them (`!token`
    //    guards). Locking would blur the app with zero recovery UI.
    // 2. An authed session boots LOCKED when either
    //      a) the persisted snapshot says it was locked (a lock, once engaged —
    //         idle timeout or the manual "Lock" menu action — survives reloads
    //         and new tabs until a real unlock), or
    //      b) the shared `lastActivity` timestamp is older than tabAwayTimeout.
    //    `lastActivity` only ever advances while UNLOCKED (useActivityTracker
    //    ignores events while locked), so a locked tab cannot freshen the
    //    timestamp and be bypassed by opening a new tab. Cross-tab: if another
    //    tab is genuinely active it keeps `lastActivity` fresh and persists
    //    isLocked=false, so new tabs open unlocked — that is legitimate, the
    //    user is present.
    // 3. Otherwise boot unlocked and stamp `lastActivity = now`.
    builder.addMatcher(
      (action) => action.type === REHYDRATE,
      (state, action: { payload?: { lock?: LockState; auth?: { token?: unknown } } }) => {
        const persistedState = action.payload?.lock;
        const now = Date.now();
        const hasAuthToken = !!action.payload?.auth?.token;

        // redux-persist's default stateReconciler (autoMergeLevel1) skips
        // merging any top-level slice its reducer modified during REHYDRATE —
        // which is always true for `lock` because we compute isLocked here.
        // So every persisted preference must be copied explicitly, otherwise
        // it silently resets to initialState on each boot (this previously
        // discarded lockEnabled/webAuthSetUp/timeout customizations).
        if (persistedState) {
          state.lockEnabled = persistedState.lockEnabled ?? state.lockEnabled;
          state.webAuthSetUp = persistedState.webAuthSetUp ?? state.webAuthSetUp;
          state.webAuthCredentialId = persistedState.webAuthCredentialId ?? state.webAuthCredentialId;
          state.lockTimeout = persistedState.lockTimeout ?? lockTimeoutDefault;
          state.tabAwayTimeout = persistedState.tabAwayTimeout ?? tabAwayTimeoutDefault;
        }
        state.showSetupPrompt = false;

        // Rule 1: guests never lock (and lock-disabled sessions never lock).
        if (!hasAuthToken || !state.lockEnabled) {
          state.isLocked = false;
          state.lastActivity = now;
          return;
        }

        // Rule 2: authed — locked if previously locked or timed out.
        const persistedLastActivity = persistedState?.lastActivity;
        const timedOut = !!persistedLastActivity
          && now - persistedLastActivity >= state.tabAwayTimeout;

        if (persistedState?.isLocked || timedOut) {
          state.isLocked = true;
          // Keep the stale timestamp so any further rehydrate (another tab,
          // another reload) reaches the same locked verdict.
          state.lastActivity = persistedLastActivity || now;
        } else {
          // Rule 3: unlocked boot counts as activity.
          state.isLocked = false;
          state.lastActivity = now;
        }
      },
    );
  },
});

export const {
  lockSession,
  unlockSession,
  requestAutoUnlock,
  clearAutoUnlock,
  setLockEnabled,
  setWebAuthSetUp,
  updateLastActivity,
  setLockTimeout,
  setShowSetupPrompt,
} = lockSlice.actions;

export default lockSlice.reducer;

// Selectors
export const selectIsLocked = (state: RootState) => state.lock.isLocked;
export const selectLockEnabled = (state: RootState) => state.lock.lockEnabled;
export const selectWebAuthSetUp = (state: RootState) => state.lock.webAuthSetUp;
export const selectWebAuthCredentialId = (state: RootState) => state.lock.webAuthCredentialId;
export const selectLastActivity = (state: RootState) => state.lock.lastActivity;
export const selectLockTimeout = (state: RootState) => state.lock.lockTimeout;
export const selectTabAwayTimeout = (state: RootState) => state.lock.tabAwayTimeout;
export const selectShowSetupPrompt = (state: RootState) => state.lock.showSetupPrompt;
export const selectAutoUnlockRequested = (state: RootState) => state.lock.autoUnlockRequested;
